/**
 * Plan comparison table (M22).
 *
 * Three-column grid (Free / Pro / Enterprise) with feature checkmark rows,
 * a "Most Popular" ribbon on the Pro tier, and a monthly/annual toggle that
 * shows "Save XX%" against monthly billing. Pricing is in INR with a
 * "+ 18% GST" footnote.
 *
 * The component is purely presentational — actual subscribe/upgrade is wired
 * by the parent (see <UpgradeDialog/>).
 */
import { Check, Minus } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PlanKey = 'free' | 'pro' | 'enterprise';

export interface PlanColumn {
  key: PlanKey;
  name: string;
  blurb: string;
  monthlyInr: number;
  annualInrPerMonth: number;
  cta: string;
  highlighted?: boolean;
}

export interface FeatureRow {
  label: string;
  values: Record<PlanKey, boolean | string>;
}

interface PlanComparisonTableProps {
  currentPlan?: PlanKey;
  onSelectPlan?: (plan: PlanKey, cycle: 'monthly' | 'annual') => void;
}

// ---------------------------------------------------------------------------
// Default plan config (kept here so the table renders without backend data)
// ---------------------------------------------------------------------------

const DEFAULT_PLANS: PlanColumn[] = [
  {
    key: 'free',
    name: 'Free',
    blurb: 'For tiny teams getting started',
    monthlyInr: 0,
    annualInrPerMonth: 0,
    cta: 'Start free',
  },
  {
    key: 'pro',
    name: 'Pro',
    blurb: 'Everything growing teams need',
    monthlyInr: 999,
    annualInrPerMonth: 799,
    cta: 'Upgrade to Pro',
    highlighted: true,
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    blurb: 'Unlimited scale + premium support',
    monthlyInr: 2499,
    annualInrPerMonth: 1999,
    cta: 'Talk to sales',
  },
];

const DEFAULT_FEATURES: FeatureRow[] = [
  { label: 'Employees', values: { free: 'Up to 10', pro: 'Up to 200', enterprise: 'Unlimited' } },
  { label: 'Core HR', values: { free: true, pro: true, enterprise: true } },
  { label: 'Attendance & Leave', values: { free: true, pro: true, enterprise: true } },
  { label: 'Payroll', values: { free: false, pro: true, enterprise: true } },
  { label: 'Recruitment', values: { free: false, pro: true, enterprise: true } },
  { label: 'Performance & Goals', values: { free: false, pro: true, enterprise: true } },
  { label: 'AI Copilot', values: { free: false, pro: true, enterprise: true } },
  { label: 'Custom roles & RBAC', values: { free: false, pro: true, enterprise: true } },
  { label: 'Advanced analytics', values: { free: false, pro: false, enterprise: true } },
  { label: 'SSO / SAML', values: { free: false, pro: false, enterprise: true } },
  { label: 'Dedicated CSM', values: { free: false, pro: false, enterprise: true } },
  { label: 'SLA-backed support', values: { free: 'Community', pro: 'Email', enterprise: '24×7' } },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function inr(amount: number): string {
  if (amount === 0) return 'Free';
  return `₹${amount.toLocaleString('en-IN')}`;
}

function pctOff(monthly: number, annual: number): number {
  if (monthly === 0) return 0;
  return Math.round(((monthly - annual) / monthly) * 100);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PlanComparisonTable({
  currentPlan,
  onSelectPlan,
}: PlanComparisonTableProps): ReactNode {
  const [cycle, setCycle] = useState<'monthly' | 'annual'>('monthly');

  const headerLabel = cycle === 'annual' ? 'per month, billed annually' : 'per month';

  const annualSavings = useMemo(() => {
    const pro = DEFAULT_PLANS.find((p) => p.key === 'pro');
    if (!pro) return 0;
    return pctOff(pro.monthlyInr, pro.annualInrPerMonth);
  }, []);

  return (
    <div className="space-y-6">
      {/* Cycle toggle */}
      <div className="flex items-center justify-center">
        <div className="inline-flex rounded-md border bg-slate-50 p-0.5 text-xs">
          <button
            type="button"
            onClick={() => setCycle('monthly')}
            className={cn(
              'rounded px-3 py-1.5 font-medium transition-colors',
              cycle === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500',
            )}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setCycle('annual')}
            className={cn(
              'rounded px-3 py-1.5 font-medium transition-colors',
              cycle === 'annual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500',
            )}
          >
            Annual{' '}
            {annualSavings > 0 && (
              <span className="ml-1 text-emerald-600">save {String(annualSavings)}%</span>
            )}
          </button>
        </div>
      </div>

      {/* Plan columns */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {DEFAULT_PLANS.map((plan) => {
          const price = cycle === 'annual' ? plan.annualInrPerMonth : plan.monthlyInr;
          const isCurrent = currentPlan === plan.key;

          return (
            <div
              key={plan.key}
              className={cn(
                'relative flex flex-col rounded-lg border bg-white p-5 transition-shadow',
                plan.highlighted ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200',
                isCurrent && 'border-emerald-500 ring-2 ring-emerald-100',
              )}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-blue-600 text-white">Most Popular</Badge>
                </div>
              )}

              <div className="mb-4">
                <h3 className="text-lg font-semibold">{plan.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{plan.blurb}</p>
              </div>

              <div className="mb-1 flex items-baseline gap-1">
                <span className="text-3xl font-bold">{inr(price)}</span>
                {price > 0 && <span className="text-xs text-muted-foreground">{headerLabel}</span>}
              </div>
              <p className="mb-4 text-[11px] text-muted-foreground">+ 18% GST</p>

              <ul className="mb-5 space-y-2 text-sm">
                {DEFAULT_FEATURES.map((row) => {
                  const v = row.values[plan.key];
                  return (
                    <li key={row.label} className="flex items-start gap-2">
                      {v === true ? (
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                      ) : v === false ? (
                        <Minus className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />
                      ) : (
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                      )}
                      <span className={v === false ? 'text-slate-400' : ''}>
                        {row.label}
                        {typeof v === 'string' && (
                          <span className="ml-1 text-xs text-muted-foreground">— {v}</span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-auto">
                <Button
                  className="w-full"
                  variant={plan.highlighted ? 'default' : 'outline'}
                  disabled={isCurrent}
                  onClick={() => onSelectPlan?.(plan.key, cycle)}
                >
                  {isCurrent ? 'Current plan' : plan.cta}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
